import type { ExperienceCapabilityV2, ExperienceConfigSchemaV2, ExperienceObjectConfigSchemaV2 } from 'sdui-core'

const string = (defaultValue: string, extras: Partial<Extract<ExperienceConfigSchemaV2, { type: 'string' }>> = {}) => ({
  type: 'string' as const,
  default: defaultValue,
  ...extras,
})
const choice = (values: readonly string[], defaultValue: string) => string(defaultValue, { enum: values })
const integer = (minimum: number, maximum: number, defaultValue: number): ExperienceConfigSchemaV2 => ({
  type: 'integer',
  minimum,
  maximum,
  default: defaultValue,
})
const toggle = (defaultValue: boolean): ExperienceConfigSchemaV2 => ({ type: 'boolean', default: defaultValue })
const strictObject = (
  properties: Readonly<Record<string, ExperienceConfigSchemaV2>>,
  required: readonly string[] = Object.keys(properties)
): ExperienceObjectConfigSchemaV2 => ({ type: 'object', additionalProperties: false, properties, required })

const responsiveInteger = (defaultValue: number) =>
  strictObject({ compact: integer(1, 12, defaultValue), web: integer(1, 12, defaultValue) })
const responsiveSurface = strictObject({
  tone: choice(['default', 'subtle', 'strong'], 'default'),
  layout: choice(['flow', 'course-grid'], 'flow'),
  maxWidth: choice(['full', '6xl'], 'full'),
  centered: toggle(false),
  padding: choice(['none', 'sm', 'md', 'lg'], 'md'),
  paddingTop: choice(['none', 'md'], 'none'),
  paddingBottom: choice(['none', 'md', 'lg'], 'none'),
  marginBottom: choice(['none', 'md'], 'none'),
})
const collection = (itemLimit: number = 12) =>
  strictObject({
    itemLimit: integer(1, 100, itemLimit),
    density: choice(['compact', 'comfortable', 'roomy'], 'comfortable'),
  })

interface CapabilityInput {
  readonly id: string
  readonly label: string
  readonly description: string
  readonly category: string
  readonly configSchema?: ExperienceObjectConfigSchemaV2
  readonly clientInputs?: readonly string[]
  readonly intents?: readonly string[]
}

const capability = (input: CapabilityInput): ExperienceCapabilityV2 => {
  const configurable = Boolean(input.configSchema)
  return {
    id: input.id,
    label: input.label,
    description: input.description,
    category: input.category,
    kind: input.id.startsWith('web.layout.') ? 'layout' : 'feature',
    platforms: ['web'],
    version: { current: 1, supported: [1] },
    configurable,
    configSchema: input.configSchema ?? strictObject({}),
    clientInputs: input.clientInputs ?? [],
    intents: input.intents ?? [],
  }
}

const inputs = (
  id: string,
  label: string,
  description: string,
  category: string,
  configSchema: ExperienceObjectConfigSchemaV2,
  clientInputs: readonly string[] = [],
  intents: readonly string[] = []
) => capability({ id, label, description, category, configSchema, clientInputs, intents })

const entries: readonly ExperienceCapabilityV2[] = [
  inputs(
    'web.layout.grid',
    'Responsive grid',
    'Arranges child blocks in responsive columns.',
    'Layout',
    strictObject({
      columns: responsiveInteger(12),
      gap: choice(['none', 'sm', 'md', 'lg'], 'md'),
    })
  ),
  inputs(
    'web.layout.grid-item',
    'Responsive column',
    'Controls a child block position inside a grid.',
    'Layout',
    strictObject({
      span: responsiveInteger(12),
      order: responsiveInteger(1),
      wrapper: choice(['box', 'contents'], 'box'),
    })
  ),
  inputs(
    'web.layout.stack',
    'Content stack',
    'Arranges child blocks in a row or column.',
    'Layout',
    strictObject({
      direction: choice(['horizontal', 'vertical'], 'vertical'),
      gap: choice(['none', 'sm', 'md', 'lg'], 'md'),
      align: choice(['start', 'center', 'end', 'stretch'], 'stretch'),
    })
  ),
  inputs(
    'web.layout.surface',
    'Page section',
    'Provides responsive spacing and width around child blocks.',
    'Layout',
    strictObject({
      compact: responsiveSurface,
      web: responsiveSurface,
    })
  ),
  inputs(
    'clientportal.events.header',
    'Events header',
    'Page title and description for events listing screens.',
    'Events',
    strictObject({
      heading: choice(['events', 'live', 'upcoming', 'past'], 'events'),
      title: string(''),
      description: string(''),
    })
  ),
  inputs(
    'clientportal.events.live-now',
    'Live events',
    'Events happening now.',
    'Events',
    collection(6),
    ['liveNowEvents'],
    ['events.open', 'events.seeAll']
  ),
  inputs(
    'clientportal.events.list',
    'Events list',
    'Upcoming and past event cards.',
    'Events',
    collection(6),
    ['upcomingEvents', 'pastEvents'],
    ['events.open', 'events.list.open']
  ),
  inputs(
    'clientportal.events.detail',
    'Event detail',
    'Overview of a single event with agenda and details card.',
    'Events',
    strictObject({
      defaultTab: choice(['about', 'tickets', 'speakers', 'attendees', 'exhibitors'], 'about'),
      gap: choice(['sm', 'md', 'lg'], 'md'),
      sidebarWidth: choice(['280', '320', '360'], '320'),
      showAbout: toggle(true),
      showAgenda: toggle(true),
      showSidebar: toggle(true),
      showTickets: toggle(true),
      showSpeakers: toggle(true),
      showAttendees: toggle(true),
      showExhibitors: toggle(true),
    }),
    ['eventDetail'],
    [
      'events.agenda.save',
      'events.agenda.addToCalendar',
      'events.register',
      'events.ticket.download',
      'events.attendee.chat',
      'events.speaker.open',
      'events.speaker.close',
      'events.exhibitor.open',
    ]
  ),
  inputs(
    'clientportal.events.directory',
    'Events directory',
    'Full live, upcoming, or past event grid.',
    'Events',
    collection(12),
    ['directoryEvents'],
    ['events.open', 'events.directory.loadMore']
  ),
]

export const CLIENTPORTAL_CAPABILITY_V2 = Object.freeze(
  Object.fromEntries(entries.map(definition => [definition.id, definition])) as Record<string, ExperienceCapabilityV2>
)
